import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WholesaleOrderInfoService } from '@jfw/angular/wholesale/services';
import {
  getCarrierServiceDisplay,
  getFormattedDateWithWeekday,
  getLineItemStatus,
  getStyleTypeFromCode,
  getTrackingUrl,
} from '@jfw/business-logic';
import {
  FulfillmentTypes,
  OrderFulfillment,
  OrderListItem,
  OrderListItemDetail,
} from '@jfw/types';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'jfw-wholesale-tracking-info-modal',
  templateUrl: './tracking-info-modal.component.html',
  styleUrls: ['./tracking-info-modal.component.scss'],
  imports: [
    MatIconModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
})
export class WholesaleTrackingInfoModalComponent {
  internalOrderNumber!: number;
  order!: OrderListItem;
  csrMode: boolean = false;
  unfulfilledItems: Array<OrderListItemDetail> = [];
  fulfillmentsMap: Map<OrderFulfillment, Array<OrderListItemDetail>> =
    new Map();
  FulfillmentTypes = FulfillmentTypes;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: {
      order: OrderListItem;
      apiUrl: string;
      csrMode?: boolean;
    },
    private orderInfoService: WholesaleOrderInfoService,
  ) {}

  ngOnInit(): void {
    this.internalOrderNumber = this.data.order.internalOrderNumber;
    this.csrMode = this.data.csrMode ?? false;
    // console.log('modal data: ', this.data);
    this.getOrderByIntNum(this.internalOrderNumber);
  }

  getStyleTypeFromCode(segmentCode: string) {
    return getStyleTypeFromCode(segmentCode);
  }

  async getOrderByIntNum(intNum: number) {
    try {
      const response = await firstValueFrom(
        this.orderInfoService.getOrderInfoByIntNum(this.data.apiUrl, intNum),
      );
      if (response) {
        this.order = response as OrderListItem;
        this.setDataForModal(this.order);
        // console.log(this.order);
      }
    } catch (error) {
      console.error('Error fetching order details', error);
    }
  }
  getFormattedDateWithWeekday(dateString: string | null | undefined) {
    return getFormattedDateWithWeekday(dateString);
  }

  openTracking(trackingNumber: string) {
    const url = getTrackingUrl(trackingNumber);
    if (url) {
      window.open(url, '_blank');
    }
  }

  isEventInPast(eventDate: string | null | undefined): boolean {
    return new Date(eventDate ?? 0).getTime() < new Date().getTime();
  }

  showExtraColumn() {
    let numberOfAllowedStatuses = 0;
    let allowedStatuses = ['P', 'G', 'H', 'A', 'B'];
    this.unfulfilledItems.forEach((item) => {
      if (allowedStatuses.includes(item.orderStatus)) {
        numberOfAllowedStatuses++;
      }
    });
    if (numberOfAllowedStatuses == 0) {
      return false;
    }
    return true;
  }

  getCarrierService(carrier?: string, carrierService?: string) {
    if (!carrier || !carrierService) {
      return 'N/A';
    } else {
      return getCarrierServiceDisplay(carrier, carrierService);
    }
  }

  setDataForModal(order: OrderListItem) {
    const assignedDetailLines = new Set<number>();
    const { fulfillments, details } = order;
    
    let dummyFulfillments: OrderFulfillment[] = []
    if (fulfillments && details) {
      for (const fulfilment of fulfillments) {
        const detailsForFulfilment: Array<OrderListItemDetail> = [];
        details.forEach((detail) => {
          if (
            detail.packingSlip === fulfilment.packingSlipNumber ||
            (detail.orderStatus === 'S' &&
              detail.packingSlip === undefined &&
              fulfilment.fulfillment === 'Customer/Dealer Has In Possession')
          ) {
            detailsForFulfilment.push(detail);
            assignedDetailLines.add(detail.line);
          }
        });
        const sortedDetailLines = detailsForFulfilment?.sort(
          (a, b) => a.line - b.line,
        );
        this.fulfillmentsMap.set(fulfilment, sortedDetailLines);
      }
    }

    if (details) {
      details
        .sort((a, b) => a.line - b.line)
        .forEach((detail) => {
          if (!detail.packingSlip && detail.orderStatus !== 'S') {
            this.unfulfilledItems.push(detail);
          } else if(
            !fulfillments ||
            !fulfillments.find(f => f.packingSlipNumber === detail.packingSlip)
           ) {
            let dummyFulfillment: OrderFulfillment = {
              packingSlipNumber: detail.packingSlip ?? 0,
              carrier: detail.carrier ?? '',
              carrierService: detail.carrierService ?? '',
              fulfillment: 'Customer/Dealer Has In Possession',
            }
            if(
              !dummyFulfillments.find(f => f.packingSlipNumber === dummyFulfillment.packingSlipNumber)
            ){
              console.log('pushing dummy: ', dummyFulfillment)
              dummyFulfillments.push(dummyFulfillment);
            }
          }
        });
    }

    if (dummyFulfillments.length > 0 && details) {
      for (const fulfilment of dummyFulfillments) {
        const detailsForFulfilment: Array<OrderListItemDetail> = [];
        details.forEach((detail) => {
          if (
            detail.packingSlip === fulfilment.packingSlipNumber ||
            (detail.orderStatus === 'S' &&
              detail.packingSlip === undefined &&
              fulfilment.fulfillment === 'Customer/Dealer Has In Possession')
          ) {
            detailsForFulfilment.push(detail);
            assignedDetailLines.add(detail.line);
          }
        });
        const sortedDetailLines = detailsForFulfilment?.sort(
          (a, b) => a.line - b.line,
        );
        this.fulfillmentsMap.set(fulfilment, sortedDetailLines);
      }
    }
  }

  getFulfillments(): Array<OrderFulfillment> {
    return Array.from(this.fulfillmentsMap.entries())
      .sort((a, b) => {
        if (a[0] && b[0]) {
          const aTime = new Date(a[0].shipDate ?? 0);
          const bTime = new Date(b[0].shipDate ?? 0);
          return bTime.getTime() - aTime.getTime();
        }
        return 0;
      })
      .map(([fulfillment]) => fulfillment);
  }

  getDetailsForFulfillment(
    fulfillment: OrderFulfillment,
  ): Array<OrderListItemDetail> | undefined {
    return this.fulfillmentsMap.get(fulfillment);
  }

  getLineItemStatus(lineStatus: string): string {
    return getLineItemStatus(lineStatus, this.csrMode);
  }
}
